/**
 * React Query hooks for orders management
 * Requirements: 16.2, 16.3
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchOrders,
  assignMasterToOrder,
  disputeOrder,
  getDisputeContext,
  resolveDispute,
  type OrderStatus,
  type OrderFilters,
  type PaginatedResponse,
  type Order,
  type DisputeReason,
  type DisputeResolution,
} from '@/lib/api';

// Query keys
export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (filters: OrderFilters) => [...orderKeys.lists(), filters] as const,
  detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch orders with optional filters
 */
export function useOrders(params: OrderFilters & { enabled?: boolean }) {
  const { enabled, ...filters } = params;

  return useQuery<PaginatedResponse<Order>, Error>({
    queryKey: orderKeys.list(filters),
    queryFn: () => fetchOrders(filters),
    enabled: enabled !== false,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to assign a master to an order
 */
export function useAssignMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, masterId }: { orderId: string; masterId: string }) =>
      assignMasterToOrder(orderId, masterId),
    onSuccess: () => {
      // Invalidate all order lists to refresh data
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
}

/**
 * Hook to dispute an order
 */
export function useDisputeOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, reason, comment }: { orderId: string; reason: DisputeReason; comment?: string }) =>
      disputeOrder(orderId, reason, comment),
    onSuccess: () => {
      // Invalidate all order lists to refresh data
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
}

// Status display helpers
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  BIDDING: 'Торги',
  ASSIGNED: 'Назначен',
  IN_PROGRESS: 'В работе',
  PAYMENT_PENDING: 'Ожидает оплаты',
  PAID_CARD: 'Оплачен картой',
  PAID_CASH: 'Оплачен наличными',
  COMPLETED: 'Завершён',
  CANCELED: 'Отменён',
  REFUNDED: 'Возврат',
  DISPUTED: 'Спор',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  BIDDING: 'bg-blue-500/10 text-blue-500',
  ASSIGNED: 'bg-purple-500/10 text-purple-500',
  IN_PROGRESS: 'bg-yellow-500/10 text-yellow-500',
  PAYMENT_PENDING: 'bg-orange-500/10 text-orange-500',
  PAID_CARD: 'bg-green-500/10 text-green-500',
  PAID_CASH: 'bg-green-500/10 text-green-500',
  COMPLETED: 'bg-emerald-500/10 text-emerald-500',
  CANCELED: 'bg-gray-500/10 text-gray-500',
  REFUNDED: 'bg-red-500/10 text-red-500',
  DISPUTED: 'bg-red-500/10 text-red-500',
};

export const ORDER_TYPE_LABELS: Record<string, string> = {
  VIDEO_CONSULT: 'Видеоконсультация',
  REPAIR: 'Ремонт',
  MAINTENANCE: 'ТО',
};

export function useDisputeContext(orderId: string | null) {
  return useQuery({
    queryKey: ['dispute-context', orderId],
    queryFn: () => getDisputeContext(orderId!),
    enabled: !!orderId,
  });
}

export function useResolveDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, resolution, refundAmount, comment }: {
      orderId: string;
      resolution: DisputeResolution;
      refundAmount?: number;
      comment?: string;
    }) => resolveDispute(orderId, resolution, refundAmount, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['dispute-context'] });
    },
  });
}
